import { z } from 'zod';
import { wordCount } from '../domain/text.js';
const skuCode = z.string().trim().min(1).max(64);
const locationLabel = z.string().trim().max(8);
export const skuAttrSchema = z.object({
    key: z.string().trim().min(1).max(60),
    value: z.string().trim().min(1).max(200),
});
export const createSkuSchema = z.object({
    code: skuCode,
    type: z.enum(['PRODUCT', 'COMPONENT']),
    nameLt: z.string().trim().min(1).max(200),
    nameEn: z.string().trim().max(200).optional(),
    attrs: z.array(skuAttrSchema).default([]),
    slaOverrideWorkdays: z.number().int().min(0).max(365).nullable().optional(),
    lowWaterQty: z.number().int().min(0).nullable().optional(),
    notes: z.string().trim().max(2000).optional(),
    locationRow: locationLabel.nullable().optional(),
    locationShelf: locationLabel.nullable().optional(),
    locationPosition: locationLabel.nullable().optional(),
});
// Live occupied code/name prefix list used by every SKU creation flow
// (WMS-50, WMS-85). `field` defaults to code for older clients.
export const skuCodeSuggestionsQuerySchema = z.object({
    prefix: z.string().trim().min(1).max(200),
    field: z.enum(['code', 'nameLt']).default('code'),
});
// `code` is now editable (customer request, 2026-06-18 / Jonas #1) — it stays
// unique per partner, validated server-side. `status` is included so the UI can
// archive a SKU (soft delete) when a hard delete is blocked by references.
export const updateSkuSchema = createSkuSchema.partial().extend({
    status: z.enum(['ACTIVE', 'ARCHIVED']).optional(),
});
// WMS-130 metadata is not a shortcut to type conversion, archive or BOM writes.
export const updateSkuMetadataSchema = updateSkuSchema.omit({ type: true, status: true }).strict();
// Quick "create a Prekė (product) from a single component" shortcut
// (customer request, 2026-06-18 / Jonas #2): makes a PRODUCT SKU whose BOM is
// just the one component at `qtyRequired` (default 1).
export const createProductFromComponentSchema = z.object({
    code: skuCode,
    nameLt: z.string().trim().min(1).max(200),
    nameEn: z.string().trim().max(200).optional(),
    qtyRequired: z.number().positive().max(1_000_000).default(1),
});
// Convert an existing SKU's type in place (customer request, 2026-06-19 /
// Jonas #6): a component becomes a product or vice versa. PRODUCT→COMPONENT
// drops the product's own BOM and is blocked while it is referenced by order
// lines (it is sold as a product).
export const convertSkuSchema = z.object({
    targetType: z.enum(['PRODUCT', 'COMPONENT']),
});
export const copyOrMoveSkuSchema = z.object({
    destinationPartnerId: z.string().uuid(),
    action: z.enum(['copy', 'move']),
    reason: z
        .string()
        .trim()
        .max(500)
        .refine((value) => wordCount(value) >= 2, { message: 'reason_too_short' }),
});
export const upsertBomLineSchema = z.object({
    componentSkuId: z.string().uuid(),
    qtyRequired: z.number().positive().max(1_000_000),
    sortOrder: z.number().int().min(0).max(999).default(0),
    comment: z.string().trim().max(500).optional(),
});
export const replaceBomSchema = z.object({
    lines: z.array(upsertBomLineSchema).max(200),
});
// Product configurator (WMS-46): find products by the components they are built
// from, so partners stop re-creating a product that already exists under
// another name. The same selection shape drives both the progressive search and
// the "this already exists" check performed before creating.
export const componentSelectionSchema = z.object({
    componentSkuId: z.string().uuid(),
    qtyRequired: z.number().positive().max(1_000_000),
});
export const matchByComponentsSchema = z.object({
    // Bounded like `replaceBomSchema.lines` — a selection is a prospective BOM.
    selection: z.array(componentSelectionSchema).min(1).max(200),
});
// Final configurator creation is deliberately a single server operation. The
// preview duplicate check is useful UX, but cannot protect against another
// request creating the same recipe before this one commits.
export const createConfiguredProductSchema = z.object({
    code: skuCode,
    nameLt: z.string().trim().min(1).max(200),
    nameEn: z.string().trim().max(200).optional(),
    selection: z.array(componentSelectionSchema).min(1).max(200),
    allowExactDuplicate: z.boolean().default(false),
});
//# sourceMappingURL=sku.js.map